import * as amplitude from '@amplitude/analytics-browser';
import { hasAnalyticsConsent } from './cookieConsent';

/**
 * Centralized Amplitude tracking utility.
 * All custom event names are defined here as a const object for compile-time safety.
 * Use the `track` function throughout the app instead of calling amplitude.track directly.
 *
 * Amplitude is a non-essential analytics tracker, so it only initialises after
 * the visitor opts in to analytics cookies (see utils/cookieConsent.ts). The
 * gate lives in two places: `startAmplitude()` (called by the consent-aware
 * bootstrap in index.tsx) never runs the SDK without consent, and every public
 * helper below short-circuits when consent is absent so no event is buffered
 * pre-consent and silently flushed once the SDK later initialises.
 */

let amplitudeStarted = false;

/**
 * Initialise the Amplitude SDK + session-replay plugin. Idempotent and a no-op
 * without analytics consent. Autocapture is deliberately narrow and Remote
 * Configuration is disabled — see the inline notes for the quota rationale.
 */
export function startAmplitude(): void {
    if (amplitudeStarted || !hasAnalyticsConsent()) return;
    amplitudeStarted = true;

    amplitude.init('aa80d1c1896826e562835ede4af98b42', undefined, {
        // SDK >= 2.16.1 enables Remote Configuration by default, which merges
        // the dashboard autocapture settings over this local config feature by
        // feature — a remotely-enabled source overrides a locally-disabled one.
        // That silently re-enabled Element Clicked, Form Started, Dead Click,
        // Web Vitals, etc. and flooded the project with events nobody charts,
        // burning the (200k/mo) event quota. Disable remote config so this
        // narrow local config is authoritative.
        remoteConfig: { fetchRemoteConfig: false },
        // Boolean `true` enables every autocapture source (Element Clicked,
        // Form Started, Web Vitals, Dead Click, etc.) which floods the project
        // with events nobody emits from the codebase. Keep only the sources we
        // actually want in the dashboard.
        autocapture: {
            attribution: true,
            pageViews: true,
            sessions: true,
            elementInteractions: false,
            formInteractions: false,
            fileDownloads: false,
            frustrationInteractions: false,
            webVitals: false,
            networkTracking: false,
        },
    });

    void import('@amplitude/plugin-session-replay-browser').then(({ sessionReplayPlugin }) => {
        // Replay draws from a separate org-wide quota. Start low (Amplitude's
        // own guidance) and raise only if 1% doesn't surface enough sessions.
        // `conservative` masks all text/inputs — required because Comtev pages
        // render guest PII (names, emails, contact details).
        amplitude.add(sessionReplayPlugin({
            sampleRate: 0.01,
            privacyConfig: { defaultMaskLevel: 'conservative' },
        }));
    });
}

/**
 * Stop sending to Amplitude when the visitor withdraws analytics consent. The
 * SDK can't be fully torn down mid-session, so we opt out (no further events,
 * replay capture halts); a full reset happens on the next page load because
 * `startAmplitude()` won't run without consent.
 */
export function stopAmplitude(): void {
    if (!amplitudeStarted) return;
    amplitude.setOptOut(true);
}

export const TrackingEvents = {
    // Auth
    ACCOUNT_CREATED: 'Account Created',
    LOGIN_COMPLETED: 'Login Completed',
    LOGIN_FAILED: 'Login Failed',
    PASSWORD_RESET_REQUESTED: 'Password Reset Requested',
    PASSWORD_RESET_COMPLETED: 'Password Reset Completed',
    INVITE_ACCEPTED: 'Invite Accepted',
    // Occasion
    OCCASION_CREATED: 'Occasion Created',
    OCCASION_UPDATED: 'Occasion Updated',
    OCCASION_DELETED: 'Occasion Deleted',
    OCCASION_SHARED: 'Occasion Shared',
    SCANNER_REQUEST_CREATED: 'Scanner Request Created',
    // Guest
    GUEST_ADDED: 'Guest Added',
    GUEST_UPDATED: 'Guest Updated',
    GUEST_DELETED: 'Guest Deleted',
    GUESTS_BULK_UPLOADED: 'Guests Bulk Uploaded',
    GUESTS_BULK_DELETED: 'Guests Bulk Deleted',
    GUEST_ASSIGNED_TO_INVITE: 'Guest Assigned to Invite',
    GUEST_ASSIGNED_TO_MULTIPLE_INVITES: 'Guest Assigned to Multiple Invites',
    WHATSAPP_MESSAGE_RETRIED: 'WhatsApp Message Retried',
    // Invite
    INVITE_CREATED: 'Invite Created',
    INVITE_UPDATED: 'Invite Updated',
    INVITE_DELETED: 'Invite Deleted',
    INVITE_COPIED: 'Invite Copied',
    INVITE_STAGE_CHANGED: 'Invite Stage Changed',
    // RSVP & Check-In
    RSVP_LIST_VIEWED: 'RSVP List Viewed',
    QR_CODE_SCANNED: 'QR Code Scanned',
    CHECK_IN_PAGE_OPENED: 'Check-In Page Opened',
    // Templates
    TEMPLATE_BROWSED: 'Template Browsed',
    TEMPLATE_PREVIEWED: 'Template Previewed',
    TEMPLATE_CREATED: 'Template Created',
    // Collaborators
    COLLABORATOR_ADDED: 'Collaborator Added',
    COLLABORATOR_REMOVED: 'Collaborator Removed',
    COLLABORATOR_GUEST_SUBMITTED: 'Collaborator Guest Submitted',
    // AI Planner
    AI_SESSION_STARTED: 'AI Session Started',
    AI_SESSION_RESUMED: 'AI Session Resumed',
    AI_MESSAGE_SENT: 'AI Message Sent',
    AI_PHASE_CHANGED: 'AI Phase Changed',
    AI_APPROVAL_APPROVED: 'AI Approval Approved',
    AI_APPROVAL_REJECTED: 'AI Approval Rejected',
    AI_VENDOR_RATED: 'AI Vendor Rated',
    AI_DASHBOARD_VIEWED: 'AI Dashboard Viewed',
    AI_PLAN_SNAPSHOT_VIEWED: 'AI Plan Snapshot Viewed',
    // Billing
    QUOTE_REQUESTED: 'Quote Requested',
    PAYMENT_INITIATED: 'Payment Initiated',
    PAYMENT_COMPLETED: 'Payment Completed',
    PAYMENT_FAILED: 'Payment Failed',
    PROMO_CODE_APPLIED: 'Promo Code Applied',
    COLLABORATOR_SLOTS_PURCHASED: 'Collaborator Slots Purchased',
    MESSAGES_PURCHASED: 'Messages Purchased',
    ORDER_VIEWED: 'Order Viewed',
    LIMIT_EXCEEDED: 'Limit Exceeded',
    // Planner client invoicing
    CLIENT_INVOICE_CREATED: 'Client Invoice Created',
    CLIENT_INVOICE_SENT: 'Client Invoice Sent',
    PLANNER_PAYOUT_REQUESTED: 'Planner Payout Requested',
    // Archival
    OCCASION_ARCHIVED_VIEW: 'Occasion Archived View',
    OCCASION_CONFLICT_ERROR: 'Occasion Conflict Error',
    // Account
    ACCOUNT_SETTINGS_UPDATED: 'Account Settings Updated',
    THEME_CHANGED: 'Theme Changed',
    NOTIFICATION_VIEWED: 'Notification Viewed',
    WORKSPACE_RAIL_TOGGLED: 'Workspace Rail Toggled',
    // First-run workspace tour
    WORKSPACE_TOUR_STARTED: 'Workspace Tour Started',
    WORKSPACE_TOUR_STEP_VIEWED: 'Workspace Tour Step Viewed',
    WORKSPACE_TOUR_COMPLETED: 'Workspace Tour Completed',
    WORKSPACE_TOUR_SKIPPED: 'Workspace Tour Skipped',
    WORKSPACE_TOUR_REPLAYED: 'Workspace Tour Replayed',
} as const;

export type TrackingEvent = typeof TrackingEvents[keyof typeof TrackingEvents];

export function track(event: TrackingEvent, properties?: Record<string, unknown>) {
    if (!hasAnalyticsConsent()) return;
    amplitude.track(event, properties);
}

export function setUserId(userId: string) {
    if (!hasAnalyticsConsent()) return;
    amplitude.setUserId(userId);
}

export function identifyUser(properties: Record<string, unknown>) {
    if (!hasAnalyticsConsent()) return;
    const identify = new amplitude.Identify();
    Object.entries(properties).forEach(([key, value]) => {
        identify.set(key, value as string);
    });
    amplitude.identify(identify);
}

export function resetUser() {
    amplitude.reset();
}
